import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { STAGES, type Stage } from "../../domain/stages.ts";
import type { BuilderApi, EmployeeDetail } from "../api.ts";
import { Link, useNav, usePolling } from "../nav.tsx";
import { ErrorNote, STAGE_LABELS, stageDot } from "../ui.tsx";
import { AvatarStage } from "./AvatarStage.tsx";
import { IdentityStage } from "./IdentityStage.tsx";
import { KnowledgeStage } from "./KnowledgeStage.tsx";
import { InterviewStage } from "./InterviewStage.tsx";
import { DistillStage } from "./DistillStage.tsx";
import { ReviewStage } from "./ReviewStage.tsx";
import { PublishStage } from "./PublishStage.tsx";
import { AssignStage } from "./AssignStage.tsx";

export type StageProps = { api: BuilderApi; detail: EmployeeDetail; reload: () => Promise<void> };

const PANELS: Record<Stage, (p: StageProps) => React.ReactNode> = {
  avatar: AvatarStage,
  identity: IdentityStage,
  knowledge: KnowledgeStage,
  interview: InterviewStage,
  distill: DistillStage,
  review: ReviewStage,
  publish: PublishStage,
  assign: AssignStage,
};

/** Jobs or sources still moving: keep polling fast so the page follows along. */
const busy = (d?: EmployeeDetail) => Boolean(d && (d.jobs.some((j) => j.status === "pending" || j.status === "running") || d.sources.some((s) => s.status === "pending" || s.status === "extracting")));

export function EmployeeShell({ api, id, stage }: { api: BuilderApi; id: string; stage?: Stage }) {
  const { go } = useNav();
  const [detail, reload, error] = usePolling(() => api.employee(id), [id], { ms: 2000, active: busy });

  if (error && !detail) return <ErrorNote error={error} />;
  if (!detail) return <Skeleton className="h-96" />;

  const current: Stage = stage ?? STAGES.find((s) => detail.stages[s] === "open") ?? "assign";
  const Panel = PANELS[current];
  const e = detail.employee;
  const latest = detail.releases[0];

  return (
    <div>
      <Button variant="ghost" size="sm" className="mb-4" onClick={() => go("/")}>
        ← Employees
      </Button>
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{e.name}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {e.jobTitle || "No job title yet"}
            {e.avatarPin ? ` · avatar ${e.avatarPin.avatarName}, voice ${e.avatarPin.voice}` : ""}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {latest ? <Badge variant="success">v{latest.version}</Badge> : <Badge variant="outline">Unreleased</Badge>}
          {detail.state.draftStale && <Badge variant="warning">Draft stale</Badge>}
          <span className="text-xs text-muted-foreground">
            {detail.usage.events} model calls{detail.usage.costUsd ? ` · $${detail.usage.costUsd.toFixed(2)}` : ""}
          </span>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-[220px_1fr]">
        <nav aria-label="Pipeline stages" className="md:sticky md:top-6 md:self-start">
          <ol className="grid gap-1">
            {STAGES.map((s, i) => {
              const status = detail.stages[s];
              const active = s === current;
              return (
                <li key={s}>
                  <Link
                    to={`/employees/${id}/${s}`}
                    className={`flex items-center justify-between gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${active ? "bg-accent text-accent-foreground" : "hover:bg-accent/50"} ${status === "locked" ? "text-muted-foreground" : ""}`}
                  >
                    <span>
                      <span className="mr-2 font-mono text-xs text-muted-foreground">{i + 1}</span>
                      {STAGE_LABELS[s].title}
                    </span>
                    {stageDot(status, status === "done" ? "Done" : status === "open" ? "Ready" : "Locked")}
                  </Link>
                </li>
              );
            })}
          </ol>
        </nav>
        <section aria-live="polite">
          <ErrorNote error={error} />
          <Panel api={api} detail={detail} reload={reload} />
        </section>
      </div>
    </div>
  );
}
